import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  ChevronDown, ChevronRight, ChevronLeft, X,
  Maximize2, Minimize2, GripVertical, ArrowUpRight, AlertTriangle, Lock,
} from 'lucide-react';

const SIZE_CLASS = {
  small: 'md:col-span-1',
  medium: 'md:col-span-2',
  large: 'md:col-span-2 xl:col-span-4',
};

/**
 * The frame every widget lives in: title, collapse, edit-mode controls,
 * skeleton / error / no-access states. The widget body itself only ever
 * receives already-computed data.
 */
const BADGE_TONE = {
  urgent: 'bg-red-100 text-red-700',
  accent: 'bg-[hsl(var(--brand-accent))]/15 text-[hsl(var(--brand-accent))]',
  calm: 'bg-emerald-100 text-emerald-700',
  muted: 'bg-surface-sunken text-fg-muted',
};

export const WidgetCard = ({
  definition, layout, payload, loading, editMode, children, badge,
  onMove, onResize, onRemove, onToggleCollapse, onDragStart, onDragOver, onDrop,
  moduleLink,
}) => {
  const key = definition.widget_key;
  const collapsed = !!layout.collapsed;
  const status = payload?.status;
  const sizeClass = SIZE_CLASS[layout.size] || SIZE_CLASS.medium;

  return (
    <Card
      className={`${sizeClass} bg-surface border-line
        transition-shadow duration-200 hover:shadow-md ${editMode ? 'ring-1 ring-[hsl(var(--brand-accent))]/40' : ''}`}
      data-testid={`widget-${key}`}
      draggable={editMode}
      onDragStart={editMode ? onDragStart : undefined}
      onDragOver={editMode ? onDragOver : undefined}
      onDrop={editMode ? onDrop : undefined}
    >
      <div className={`flex items-center gap-2 px-4 py-3 ${collapsed ? '' : 'border-b border-line-subtle'}`}>
        {editMode && (
          <GripVertical className="w-4 h-4 text-fg-muted shrink-0 cursor-grab" aria-hidden="true" />
        )}

        {/* Outside edit mode the whole header is the expand/collapse button, so a
            collapsed card reads as one tappable row: title + count. */}
        {editMode ? (
          <h2 className="text-base font-semibold text-fg truncate flex-1" title={definition.title}>
            {definition.title}
          </h2>
        ) : (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 flex-1 text-left rounded
              focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-[hsl(var(--brand-accent))]"
            data-testid={`widget-${key}-collapse`}
          >
            {collapsed ? <ChevronRight className="w-4 h-4 text-fg-muted shrink-0" />
                       : <ChevronDown className="w-4 h-4 text-fg-muted shrink-0" />}
            <span className="text-base font-semibold text-fg truncate min-w-[6.5rem]"
              title={definition.title}>
              {definition.title}
            </span>
            {badge && (
              <span
                className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${BADGE_TONE[badge.tone] || BADGE_TONE.muted}`}
                data-testid={`widget-${key}-badge`}
              >
                {badge.text}
              </span>
            )}
          </button>
        )}

        {editMode ? (
          <div className="flex items-center gap-0.5 shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7"
              aria-label={`Move ${definition.title} earlier`}
              data-testid={`widget-${key}-move-up`} onClick={() => onMove(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7"
              aria-label={`Move ${definition.title} later`}
              data-testid={`widget-${key}-move-down`} onClick={() => onMove(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7"
              aria-label={`Make ${definition.title} smaller`}
              data-testid={`widget-${key}-shrink`} onClick={() => onResize(-1)}>
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7"
              aria-label={`Make ${definition.title} bigger`}
              data-testid={`widget-${key}-grow`} onClick={() => onResize(1)}>
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon"
              className="h-7 w-7 text-red-600 hover:text-red-700"
              aria-label={`Remove ${definition.title}`}
              data-testid={`widget-${key}-remove`} onClick={onRemove}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          moduleLink && !collapsed && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-fg-muted shrink-0"
              aria-label={`Open ${definition.title} in full`}
              data-testid={`widget-${key}-open`} onClick={moduleLink}>
              <ArrowUpRight className="w-4 h-4" />
            </Button>
          )
        )}
      </div>

      {!collapsed && (
        <CardContent className="p-4" data-testid={`widget-${key}-body`}>
          {loading && <WidgetSkeleton size={layout.size} />}
          {!loading && status === 'error' && (
            <div className="flex items-start gap-2 text-sm text-amber-700"
              data-testid={`widget-${key}-error`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>This widget could not load. Everything else on the page is fine.</span>
            </div>
          )}
          {!loading && status === 'no_access' && (
            <div className="flex items-start gap-2 text-sm text-fg-muted"
              data-testid={`widget-${key}-no-access`}>
              <Lock className="w-4 h-4 mt-0.5 shrink-0" />
              <span>You no longer have access to this information.</span>
            </div>
          )}
          {!loading && status === 'ok' && children}
        </CardContent>
      )}
    </Card>
  );
};

export const WidgetSkeleton = ({ size = 'medium' }) => (
  <div className="space-y-2.5" aria-hidden="true">
    {[...Array(size === 'small' ? 2 : size === 'large' ? 5 : 3)].map((_, i) => (
      <div key={i} className="h-4 rounded skeleton-pulse bg-surface-sunken"
        style={{ width: `${100 - i * 12}%` }} />
    ))}
  </div>
);

export const WidgetEmpty = ({ children, testId }) => (
  <p className="text-sm text-fg-muted py-2" data-testid={testId}>{children}</p>
);
